import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import useApi from "../useApi";

export interface IAffectData {
  depressionScore: "low" | "high";
  anxietyScore: "low" | "high";
}

interface IAffectRes {
  msg: string;
  data: IAffectData;
  status: string;
}

const useFetchAffectPage = (billingCycleId: string) => {
  const [affectData, setAffectData] = useState<IAffectData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { makeApiCall } = useApi({
    url: `/rtm/patient-dashboard/cron/pdf/v3/affect/${billingCycleId}`,
    method: "GET",
    preAuth: true,
  });

  const getAffectData = useCallback(async () => {
    if (!billingCycleId) {
      console.log("No patientId provided, skipping affect data fetch");
      return null;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { response, error: apiError } = await makeApiCall<
        undefined,
        IAffectRes
      >();

      if (apiError) {
        throw new Error(apiError.msg || "Failed to fetch affect data");
      }

      if (response?.status === "success") {
        setAffectData(response.data);
        return response.data;
      } else {
        throw new Error(response?.msg || "No affect data available");
      }
    } catch (error: any) {
      console.error("Error in getAffectData:", error);
      setError(error.message);
      // toast.error(error.message || "Unable to load affect data. Please refresh and try again.");
      setAffectData(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [billingCycleId, makeApiCall]);
  useEffect(() => {
    if (billingCycleId) {
      getAffectData();
    }
  }, [billingCycleId, getAffectData]);

  return {
    affectData,
    isLoading,
    error,
    refetch: getAffectData,
  };
};

export default useFetchAffectPage;
